import type { Account } from "@tandemdiabetes/sugarmate-api-react-client"
import { Trend } from "@tandemdiabetes/sugarmate-api-react-client"
import dayjs from "dayjs"
import { useMemo } from "react"
import styles from "./AccountReading.module.scss"
import { TrendIcon } from "src/components"
import {
  usePolyglot,
  useRecentReadings,
} from "src/contexts"
import {
  useDisplayGlucoseValue,
  useReadableGlucoseUnit,
  useThisMinute,
} from "src/hooks"

interface Props {
  accountId: Account["id"];
}

export function AccountReading(props: Props) {
  const { readings } = useRecentReadings()
  const reading = readings[props.accountId]

  const thisMinute = useThisMinute()
  const polyglot = usePolyglot()
  const displayGlucoseValue = useDisplayGlucoseValue()
  const unit = useReadableGlucoseUnit()

  const minutesSinceReading = useMemo(
    () => reading
      ? dayjs(thisMinute).diff(
        reading.startedAt,
        "minute",
      )
      : null,
    [
      reading,
      thisMinute,
    ],
  )

  return (
    <div className={styles.container}>
      {reading && minutesSinceReading !== null && minutesSinceReading < 15
        ? (
          <>
            <div className={styles.readingContainer}>
              <div
                className={minutesSinceReading < 10 ? styles.time : styles.nearlyStaleData}
                id="menu-cgm-reading-time"
              >
                {polyglot.t(
                  "time.min_ago",
                  Math.max(
                    0,
                    minutesSinceReading ?? 0,
                  ),
                )}
              </div>
              <div
                className={styles.reading}
                id="menu-cgm-reading-value"
              >
                {displayGlucoseValue(
                  reading.glucose.mg_dl,
                  reading.glucose.special_value,
                )}
              </div>
              <div
                className={styles.measurement}
                id="menu-cgm-reading-units"
              >
                {unit}
              </div>
            </div>
            <TrendIcon
              className={styles.trendIcon}
              id="menu-cgm-trend-arrow"
              trend={reading.glucose.trend ?? Trend.None}
            />
          </>
        )
        : (
          <>
            <div className={styles.readingContainer}>
              <div className={styles.noData}>
                {polyglot.t("time.no_data")}
              </div>
              <div className={styles.reading}>
                ---
              </div>
              <div className={styles.measurement}>
                {unit}
              </div>
            </div>
            <div className={styles.trendIcon} />
          </>
        )
      }
    </div>
  )
}
